// Employee Dashboard — sales-performance-first view of employees (mirrors
// VendorDashboardScreen.tsx / SalesDashboardScreen.tsx's pattern), with
// attendance folded in as a secondary signal. Wired to the Employee-specific
// backend aggregation queries via ONE combined GraphQL round trip
// (getEmployeeDashboardDataAction).
//
// The Sales by Employee chart intentionally does NOT respect the employee
// filter dropdown — same precedent as Vendor Dashboard's "Purchase Amount by
// Vendor" chart, which always shows all vendors regardless of the vendor
// filter. Only the KPI summary and the Employee Performance table narrow to
// a single employee when one is selected.
import * as React from "react";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import { ColumnDef } from "@tanstack/react-table";
import { Users, UserCheck, UserX, UserPlus, IndianRupee, Receipt, CalendarCheck } from "lucide-react";
import AppConstant from "../../../Globals/AppConstants";
import { getEmployeeDashboardDataAction, employeeDropDownListAction } from "../../../Store/actions/apparelAction";
import ApparelMenu from "../../SubComponents/apparelMenu";
import { StatCard } from "../../../shared/stat-card";
import { DataTable } from "../../../shared/data-table";
import { Skeleton } from "ui/skeleton";
import { Combobox } from "ui/combobox";
import { ChartCard } from "./ChartCard";
import { CategoryBarChart } from "./CategoryBarChart";
import { CategoryDonutChart } from "./CategoryDonutChart";

const inr = (v: any) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(Number(v) || 0);
const num = (v: any) => new Intl.NumberFormat("en-IN").format(Number(v) || 0);
const pct = (v: any) => `${(Number(v) || 0).toFixed(1)}%`;
const PAGE_SIZE = 10;

const getFinancialYearRange = () => {
  const today = new Date();
  const startYear = today.getMonth() >= 3 ? today.getFullYear() : today.getFullYear() - 1;
  return {
    fromDate: moment({ year: startYear, month: 3, day: 1 }).format("YYYY-MM-DD"),
    toDate: moment({ year: startYear + 1, month: 2, day: 31 }).format("YYYY-MM-DD"),
    label: `Apr ${startYear} – Mar ${startYear + 1}`,
  };
};

const EmployeeDashboardScreen = () => {
  const dispatch = useDispatch();
  const apparelReducerState: any = useSelector((state: any) => state.ApparelReducerState);
  const { getEmployeeDashboardData, getEmployeeDashboardDataLoad, genEmployeeDropDownListData } = apparelReducerState;

  const fy = React.useMemo(() => getFinancialYearRange(), []);
  const [employeeId, setEmployeeId] = React.useState<string>("");
  const [page, setPage] = React.useState(1);

  const employeeOptions = (genEmployeeDropDownListData?.getEmployeeDropDownList || []).map((e: any) => ({
    value: String(e?.employeeId),
    label: e?.name,
  }));

  const fetchDashboard = React.useCallback(
    (selectedEmployeeId?: string, selectedPage?: number) => {
      const p = selectedPage || 1;
      dispatch(
        getEmployeeDashboardDataAction({
          employeeId: selectedEmployeeId || "",
          fromDate: fy.fromDate,
          toDate: fy.toDate,
          offset: (p - 1) * PAGE_SIZE,
        })
      );
    },
    [dispatch, fy]
  );

  React.useEffect(() => {
    dispatch(employeeDropDownListAction());
    fetchDashboard(employeeId, page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onEmployeeChange = (nextEmployeeId: string) => {
    setEmployeeId(nextEmployeeId);
    setPage(1);
    fetchDashboard(nextEmployeeId, 1);
  };

  const onPageChange = (nextPage: number) => {
    setPage(nextPage);
    fetchDashboard(employeeId, nextPage);
  };

  const summary = getEmployeeDashboardData?.getEmployeeSummary;
  const loading = getEmployeeDashboardDataLoad && !summary;
  const chartsLoading = getEmployeeDashboardDataLoad && !getEmployeeDashboardData;

  const salesByEmployee = (getEmployeeDashboardData?.getSalesByEmployee || []).map((r: any) => ({
    label: r.employeeName,
    value: Number(r.amount) || 0,
  }));

  const attendanceDistribution = (getEmployeeDashboardData?.getAttendanceDistribution || []).map((r: any) => ({
    category: r.status,
    amount: Number(r.count) || 0,
  }));

  const employeePerformance = getEmployeeDashboardData?.getEmployeePerformance?.employeeDetails || [];
  const employeePerformancePage = getEmployeeDashboardData?.getEmployeePerformance?.page;

  const performanceCsvRows = React.useMemo(
    () => employeePerformance.map((r: any) => ({ ...r, attendanceRate_fmt: pct(r.attendanceRate) })),
    [employeePerformance]
  );

  const performanceColumns = React.useMemo<ColumnDef<any, any>[]>(
    () => [
      { id: "employeeCode", header: "ID", cell: ({ row }) => row.original?.employeeCode || "-" },
      { id: "employeeName", header: "Employee", cell: ({ row }) => row.original?.employeeName },
      { id: "totalSales", header: "Sales", cell: ({ row }) => <span className="tabular-nums">{inr(row.original?.totalSales)}</span> },
      { id: "totalOrders", header: "Orders", cell: ({ row }) => <span className="tabular-nums">{num(row.original?.totalOrders)}</span> },
      { id: "aov", header: "Avg Order Value", cell: ({ row }) => <span className="tabular-nums">{inr(row.original?.aov)}</span> },
      { id: "attendanceRate", header: "Attendance %", cell: ({ row }) => <span className="tabular-nums">{pct(row.original?.attendanceRate)}</span> },
      {
        id: "leaveDaysUsed",
        header: "Leave Used",
        cell: ({ row }) => (
          <span className="tabular-nums">
            {num(row.original?.leaveDaysUsed)}
            {row.original?.allowedLeaveDays != null ? ` / ${num(row.original?.allowedLeaveDays)}` : ""}
          </span>
        ),
      },
    ],
    []
  );

  return (
    <div className="mz h-full overflow-y-auto px-6 py-4">
      <ApparelMenu selectedKey="4" />

      <div className="mb-4 flex items-center gap-1 border-b">
        <button type="button" className="border-b-2 border-primary px-3 py-2 text-sm font-medium text-foreground">
          {AppConstant.menus.employeeDashboard}
        </button>
      </div>

      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">Showing current financial year ({fy.label}).</p>
        <Combobox
          options={employeeOptions}
          value={employeeId || undefined}
          onValueChange={onEmployeeChange}
          placeholder="All Employees"
          searchPlaceholder="Search employee..."
          className="w-[220px]"
        />
      </div>

      {/* KPI cards — two rows of three, wide enough that titles/values never
          overlap or truncate (six-across was too narrow per card). */}
      <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {loading ? (
          Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-24 w-full rounded-xl" />)
        ) : (
          <>
            <StatCard label="Active Employees" value={num(summary?.activeEmployees)} icon={UserCheck} />
            <StatCard
              label="Total Sales"
              value={inr(summary?.totalSales)}
              icon={IndianRupee}
              valueClassName="text-xl !overflow-visible !whitespace-normal !break-words"
            />
            <StatCard label="Total Orders" value={num(summary?.totalOrders)} icon={Receipt} />
            <StatCard
              label="Average Order Value"
              value={inr(summary?.aov)}
              icon={IndianRupee}
              valueClassName="text-xl !overflow-visible !whitespace-normal !break-words"
            />
            <StatCard label="Attendance Rate" value={pct(summary?.attendanceRate)} icon={CalendarCheck} />
            <StatCard label="New Employees" value={num(summary?.newEmployees)} icon={UserPlus} />
          </>
        )}
      </div>

      {/* Charts */}
      <div className="mb-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
        <ChartCard title="Sales by Employee" loading={chartsLoading} isEmpty={!salesByEmployee.length}>
          <CategoryBarChart data={salesByEmployee} valueFormatter={inr} layout="vertical" />
        </ChartCard>
        <ChartCard title="Attendance Distribution" loading={chartsLoading} isEmpty={!attendanceDistribution.length}>
          <CategoryDonutChart data={attendanceDistribution} valueFormatter={num} />
        </ChartCard>
      </div>

      {/* Table */}
      <div className="mb-2 flex flex-wrap gap-1 border-b">
        <button type="button" className="border-b-2 border-primary px-3 py-2 text-sm font-medium text-foreground">
          Employee Performance
        </button>
      </div>
      <DataTable
        columns={performanceColumns}
        data={employeePerformance}
        loading={chartsLoading}
        csvExport={{
          fileName: `employee-performance_${moment().format("YYYY-MM-DD")}`,
          rows: performanceCsvRows,
          columns: [
            { key: "employeeCode", header: "ID" },
            { key: "employeeName", header: "Employee" },
            { key: "totalSales", header: "Sales" },
            { key: "totalOrders", header: "Orders" },
            { key: "aov", header: "Avg Order Value" },
            { key: "attendanceRate_fmt", header: "Attendance %" },
            { key: "leaveDaysUsed", header: "Leave Used" },
            { key: "allowedLeaveDays", header: "Leave Allowed" },
          ],
        }}
        pagination={{
          page: employeePerformancePage?.currentPage || page,
          total: employeePerformancePage?.totalCount || 0,
          pageSize: PAGE_SIZE,
          onChange: onPageChange,
        }}
      />
    </div>
  );
};

export default EmployeeDashboardScreen;
